/**
 * `text` — `TextValue` (`specs/M2-tier1-column-types.md` §5.3, `[09§A.7 text]`).
 *
 * Write shape is a bare JSON string: `{"text": "Sample text"}`. The stored `value` is a
 * JSON-encoded **string** (`"\"SKU-1000\""`), not a bare string `[cvs§2 text]`, which is exactly
 * what `JSON.stringify(toMondayValue(v))` produces. `text` was **never null** for this type on
 * this account (0 null, 2,608 empty), so an empty cell renders `""`.
 */
import { z } from 'zod';
import { defaultFilterSql, defaultSummarySql } from '../sql.js';
import { textShadow } from '../helpers.js';
import { ColumnValueError, defineColumnType } from '../types.js';
/** *"approximate 64KB total column value capacity"* per item `[09§A.7 text]`. */
export const TEXT_MAX_LENGTH = 65_536;
export const textValueSchema = z.string().max(TEXT_MAX_LENGTH);
export const textSettingsSchema = z.looseObject({
    hide_footer: z.boolean().optional(),
});
import { textFilterConditions } from './filters.js';
import { operatorsOf } from '../filter-conditions.js';
function coerce(raw) {
    if (raw === null || raw === undefined)
        return null;
    if (typeof raw === 'string')
        return raw === '' ? null : raw;
    if (typeof raw === 'number' || typeof raw === 'boolean')
        return String(raw);
    if (typeof raw === 'object') {
        const candidate = raw.text;
        if (candidate === null || candidate === undefined)
            return null;
        if (typeof candidate === 'string')
            return candidate === '' ? null : candidate;
    }
    throw new ColumnValueError('text', `expected a string, received ${typeof raw}`);
}
function validate(value) {
    if (value === null)
        return null;
    const parsed = textValueSchema.safeParse(value);
    if (!parsed.success) {
        throw new ColumnValueError('text', `value exceeds the ${TEXT_MAX_LENGTH} character capacity`);
    }
    return parsed.data;
}
export const textColumn = defineColumnType({
    type: 'text',
    title: 'Text',
    description: 'A single line of free text.',
    apiFragmentName: 'TextValue',
    legacyTypeName: 'text',
    idPrefix: 'text',
    valueSchema: textValueSchema,
    settingsSchema: textSettingsSchema,
    defaultSettings: {},
    settingsStrSchema: textSettingsSchema,
    writable: true,
    supportsSimpleValue: true,
    clearForms: ['null', 'empty-string'],
    filterConditions: textFilterConditions,
    filterOperators: operatorsOf(textFilterConditions),
    aggregations: ['count_filled', 'count_empty', 'count', 'none'],
    emptyText: '',
    parseValue(raw) {
        return validate(coerce(raw));
    },
    parseSimpleValue(raw) {
        return validate(coerce(raw));
    },
    toText(value) {
        return value ?? '';
    },
    toSortKeys(value) {
        return { text: textShadow(value), number: null, date: null };
    },
    isEqual(a, b) {
        return (a ?? null) === (b ?? null);
    },
    toActivityValue(value) {
        return value;
    },
    fromMondayValue(raw) {
        return coerce(raw);
    },
    toMondayValue(value) {
        return value;
    },
    toSimpleValue(value) {
        return value ?? '';
    },
    fromMondaySettings(raw) {
        return textSettingsSchema.parse(raw ?? {});
    },
    toMondaySettings(settings) {
        return { ...settings };
    },
    toMondaySettingsStr(settings) {
        return { ...settings };
    },
    toApiFragment() {
        // `TextValue` adds no fields beyond the interface `[cvs§2 text]`.
        return {};
    },
    /**
     * Recorded: `{"text": {"value": "probe"}}` in the `create_item` delivery `[whk§7,§8]` —
     * the string wrapped in `value`, where `ColumnValue.value` is the JSON-encoded string.
     */
    toWebhookValue(value) {
        return value === null ? null : { value };
    },
    bucketsOf(value, settings) {
        const text = this.toText(value, settings) ?? '';
        return text === '' ? [] : [{ key: text, label: text }];
    },
    filterSql(ctx) {
        return defaultFilterSql('text', ctx);
    },
    summarySql(ctx) {
        return defaultSummarySql(ctx);
    },
});
//# sourceMappingURL=index.js.map